import * as THREE from 'three';

/**
 * StarField - Creates a realistic starry background for the solar system simulation
 * Generates thousands of stars at various distances and brightnesses to simulate deep space
 */
export class StarField {
    constructor(starCount = 5000) {
        this.starCount = starCount;
        this.starField = null;
        this.geometry = null;
        this.material = null;
        this.isVisible = false;

        // Star generation parameters
        // Positioned at realistic interstellar distances with appropriate sizes
        this.config = {
            minDistance: 100000, // Much farther for realistic scale
            maxDistance: 400000, // Very distant stars
            minSize: 0.5, // Small point-like stars
            maxSize: 2.0, // Maximum size for bright stars
            minBrightness: 0.4, // Adequate minimum brightness
            maxBrightness: 1.0, // Full brightness for bright stars
            colorVariation: true, // Enable star color variation
            twinkling: false, // Enable star twinkling effect
            galacticStructure: false, // Enable realistic Milky Way structure
            galacticMotion: false, // Enable subtle galactic rotation
        };

        // Star colors based on stellar classification
        this.starColors = [
            0xffffff, // White (G-type, like our Sun)
            0xffe4b5, // Light yellow (F-type)
            0xffd700, // Yellow (G-type)
            0xffa500, // Orange (K-type)
            0xff6347, // Red-orange (M-type)
            0x87ceeb, // Light blue (A-type)
            0x4169e1, // Blue (B-type)
            0x0000ff, // Deep blue (O-type)
        ];

        console.log(`StarField initialized with ${starCount} stars`);
    }

    /**
     * Create the star field geometry and add to scene
     * Stars are positioned at realistic interstellar distances (10,000-50,000 units)
     * to simulate the actual scale difference between planetary and stellar distances
     * @param {THREE.Scene} scene - Three.js scene to add stars to
     */
    createStarField(scene) {
        this.scene = scene;

        // Create geometry for stars
        this.geometry = new THREE.BufferGeometry();

        // Arrays to hold star data
        const positions = new Float32Array(this.starCount * 3);
        const colors = new Float32Array(this.starCount * 3);
        const sizes = new Float32Array(this.starCount);

        // Generate random stars
        for (let i = 0; i < this.starCount; i++) {
            // Generate random position on a sphere
            const phi = Math.random() * Math.PI * 2; // Azimuth angle
            const theta = Math.acos(2 * Math.random() - 1); // Polar angle (uniform distribution)

            // Random distance within our range
            const distance =
                this.config.minDistance +
                Math.random() * (this.config.maxDistance - this.config.minDistance);

            // Convert spherical to cartesian coordinates
            const x = distance * Math.sin(theta) * Math.cos(phi);
            const y = distance * Math.sin(theta) * Math.sin(phi);
            const z = distance * Math.cos(theta);

            positions[i * 3] = x;
            positions[i * 3 + 1] = y;
            positions[i * 3 + 2] = z;

            // Generate star color
            let starColor;
            if (this.config.colorVariation) {
                // Use stellar classification colors with weighted distribution
                const colorIndex = this.getWeightedStarColorIndex();
                starColor = new THREE.Color(this.starColors[colorIndex]);
            } else {
                starColor = new THREE.Color(0xffffff); // All white stars
            }

            // Apply brightness variation
            const brightness =
                this.config.minBrightness +
                Math.random() * (this.config.maxBrightness - this.config.minBrightness);

            starColor.multiplyScalar(brightness);

            colors[i * 3] = starColor.r;
            colors[i * 3 + 1] = starColor.g;
            colors[i * 3 + 2] = starColor.b;

            // Generate star size (apparent magnitude)
            const size =
                this.config.minSize + Math.random() * (this.config.maxSize - this.config.minSize);
            sizes[i] = size;
        }

        // Set geometry attributes
        this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        this.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        this.geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

        // Create star material
        this.material = new THREE.PointsMaterial({
            size: 1.5, // Smaller base size for point-like appearance
            transparent: true,
            opacity: 0.9, // Slightly transparent for realistic look
            vertexColors: true,
            sizeAttenuation: false, // Stars don't get smaller with distance
            blending: THREE.AdditiveBlending, // Makes stars glow
        });

        // Create the star field object
        this.starField = new THREE.Points(this.geometry, this.material);
        this.starField.name = 'starField';
        this.starField.visible = this.isVisible;

        // Add to scene
        this.scene.add(this.starField);

        console.log('Star field created and added to scene');
    }

    /**
     * Apply realistic galactic orientation based on our position in the Milky Way
     * Rotates the entire star field to match real galactic coordinates
     */
    applyGalacticOrientation() {
        if (!this.starField || !this.geometry) return;

        // Rotación para orientar correctamente la galaxia
        // El centro galáctico está hacia Sagitario (RA ~17h 45m, Dec ~-29°)
        // El polo norte galáctico está hacia Coma Berenices (RA ~12h 51m, Dec ~+27°)

        // Aplicar rotación para orientación galáctica realista
        const galacticRotationY = Math.PI * 0.4; // ~72° rotación hacia Sagitario
        const galacticRotationX = -Math.PI * 0.16; // ~-29° inclinación del plano galáctico
        const galacticRotationZ = Math.PI * 0.05; // Pequeña rotación para el Brazo de Orión

        // Crear matriz de rotación compuesta
        const rotationMatrix = new THREE.Matrix4();
        rotationMatrix.makeRotationFromEuler(
            new THREE.Euler(galacticRotationX, galacticRotationY, galacticRotationZ, 'XYZ')
        );

        // Aplicar rotación a todo el campo de estrellas
        this.starField.applyMatrix4(rotationMatrix);

        console.log('Applied realistic galactic orientation - center toward Sagittarius');
    }

    /**
     * Get weighted star color index based on stellar classification frequency
     * More common star types (like red dwarfs) appear more frequently
     * @returns {number} Index into starColors array
     */
    getWeightedStarColorIndex() {
        const random = Math.random();

        // Weighted distribution based on actual stellar populations
        if (random < 0.76) return 4; // M-type (red dwarfs) - most common
        if (random < 0.88) return 3; // K-type (orange dwarfs)
        if (random < 0.96) return 0; // G-type (yellow dwarfs, like our Sun)
        if (random < 0.98) return 1; // F-type (yellow-white)
        if (random < 0.994) return 5; // A-type (white)
        if (random < 0.999) return 6; // B-type (blue-white)
        return 7; // O-type (blue giants) - very rare
    }

    /**
     * Show the star field
     */
    show() {
        this.isVisible = true;
        if (this.starField) {
            this.starField.visible = true;
        }
        console.log('Star field shown');
    }

    /**
     * Hide the star field
     */
    hide() {
        this.isVisible = false;
        if (this.starField) {
            this.starField.visible = false;
        }
        console.log('Star field hidden');
    }

    /**
     * Toggle star field visibility
     */
    toggle() {
        if (this.isVisible) {
            this.hide();
        } else {
            this.show();
        }
    }

    /**
     * Check if star field is visible
     * @returns {boolean} True if visible
     */
    isStarFieldVisible() {
        return this.isVisible;
    }

    /**
     * Update star field (for twinkling effect and galactic motion if enabled)
     * @param {number} deltaTime - Time since last update
     */
    update(deltaTime) {
        if (!this.starField || !this.isVisible) return;

        // Twinkling effect
        if (this.config.twinkling) {
            const time = performance.now() * 0.001;
            const twinkleIntensity = 0.1;
            const baseOpacity = 0.9;
            this.material.opacity = baseOpacity + Math.sin(time * 2) * twinkleIntensity;
        }

        // Simulate very slow galactic rotation (optional, very subtle)
        if (this.config.galacticMotion && deltaTime) {
            const rotationSpeed = 0.000001; // Extremely slow rotation
            this.starField.rotateY(rotationSpeed * deltaTime);
        }
    }

    /**
     * Centra el campo de estrellas en una posición (técnica de "skybox"). Al moverlo cada frame
     * con la cámara, las estrellas distantes NO muestran paralaje por la traslación heliocéntrica
     * (aparecen "en el infinito") y nunca se salen de rango. La rotación se conserva, así que
     * girar la cámara sí revela distintas partes de la Vía Láctea.
     * @param {THREE.Vector3} position - Posición sobre la que centrar (normalmente la cámara)
     */
    centerOn(position) {
        if (this.starField && position) {
            this.starField.position.copy(position);
        }
    }

    /**
     * Set star field configuration
     * @param {Object} config - Configuration options
     */
    setConfig(config) {
        Object.assign(this.config, config);

        // If star field exists, recreate it with new config
        if (this.starField && this.scene) {
            this.dispose();
            if (config.galacticStructure) {
                this.createRealisticDistribution(this.scene);
            } else {
                this.createStarField(this.scene);
            }
        }
    }

    /**
     * Enable or disable galactic structure simulation
     * @param {boolean} enabled - Whether to use realistic galactic distribution
     */
    setGalacticStructure(enabled) {
        this.config.galacticStructure = enabled;

        if (this.starField && this.scene) {
            this.dispose();
            if (enabled) {
                this.createRealisticDistribution(this.scene);
            } else {
                this.createStarField(this.scene);
            }
        }
    }

    /**
     * Check if galactic structure is enabled
     * @returns {boolean} True if galactic structure is enabled
     */
    isGalacticStructureEnabled() {
        return this.config.galacticStructure || false;
    }

    /**
     * Enable or disable twinkling effect
     * @param {boolean} enabled - Whether twinkling should be enabled
     */
    setTwinkling(enabled) {
        this.config.twinkling = enabled;
    }

    /**
     * Set star count and regenerate field
     * @param {number} count - Number of stars to generate
     */
    setStarCount(count) {
        this.starCount = Math.max(100, Math.min(20000, count)); // Clamp to reasonable range

        if (this.starField && this.scene) {
            this.dispose();
            if (this.config.galacticStructure) {
                this.createRealisticDistribution(this.scene);
            } else {
                this.createStarField(this.scene);
            }
        }
    }

    /**
     * Get star field statistics
     * @returns {Object} Statistics about the star field
     */
    getStatistics() {
        return {
            starCount: this.starCount,
            isVisible: this.isVisible,
            hasTwinkling: this.config.twinkling,
            hasGalacticStructure: this.config.galacticStructure,
            hasGalacticMotion: this.config.galacticMotion,
            memoryUsage: this.starCount * (3 + 3 + 1) * 4, // positions + colors + sizes in bytes
            config: { ...this.config },
            galacticInfo: {
                centerDirection: 'Sagittarius (negative X axis)',
                solarPosition: 'Orion Arm, ~26,000 ly from center',
                galacticRotation: 'Counterclockwise (viewed from north pole)',
                solarVelocity: '~220 km/s toward Hercules/Lyra',
            },
        };
    }

    /**
     * Create a more realistic star distribution based on Milky Way structure
     * Simulates galactic center, spiral arms, and stellar density gradients
     * @param {THREE.Scene} scene - Three.js scene to add stars to
     */
    createRealisticDistribution(scene) {
        if (scene) {
            this.scene = scene;
        }

        if (!this.scene) {
            console.warn('Scene not available for realistic distribution');
            return;
        }

        // Dispose existing star field
        if (this.starField) {
            this.dispose();
        }

        // Create geometry for stars
        this.geometry = new THREE.BufferGeometry();

        // Arrays to hold star data
        const positions = new Float32Array(this.starCount * 3);
        const colors = new Float32Array(this.starCount * 3);
        const sizes = new Float32Array(this.starCount);

        // Galactic parameters based on real Milky Way structure
        // Sol está en el Brazo de Orión, a ~26,000 años luz del centro
        // Centro galáctico está en dirección a Sagitario (hacia el "lado" del sistema solar)
        const galacticCenter = { x: -200000, y: 0, z: 0 }; // Center to the "side" (Sagittarius direction)
        const galacticRadius = 300000; // Realistic galaxy radius
        const diskThickness = 20000; // Realistic disk thickness
        const solarPosition = { x: 0, y: 0, z: 0 }; // Our position in the Orion Arm

        // Solar motion parameters (heliocentric movement)
        const solarVelocity = { x: 0, y: 0, z: 220 }; // ~220 km/s toward Hercules/Lyra
        const galacticRotation = 0.001; // Slow galactic rotation effect

        // Generate stars with galactic structure
        for (let i = 0; i < this.starCount; i++) {
            let x, y, z, density;

            // Determine star type and position based on galactic structure
            const random = Math.random();

            if (random < 0.15) {
                // Galactic center region (15% of stars) - toward Sagittarius
                const centerDist = Math.random() * 80000 + 120000;
                const centerAngle = Math.random() * Math.PI * 2;
                const centerHeight = (Math.random() - 0.5) * 10000;

                // Center is toward negative X (Sagittarius direction)
                x = galacticCenter.x + Math.cos(centerAngle) * centerDist;
                y = centerHeight;
                z = galacticCenter.z + Math.sin(centerAngle) * centerDist;
                density = 3.0; // High density
            } else if (random < 0.65) {
                // Spiral arms (50% of stars) - Orion, Perseus, Sagittarius-Carina arms
                const armAngle = Math.random() * Math.PI * 2;
                const armRadius = 100000 + Math.random() * 200000;
                const armWidth = 20000;

                // Create spiral pattern with realistic galactic rotation
                // Vía Láctea rota en sentido antihorario visto desde el polo norte
                const spiralTightness = 0.2; // Más abierto, como la Vía Láctea real
                const spiralOffset = armAngle * spiralTightness;

                // Ajustar para orientación galáctica real
                const rotatedAngle = armAngle + spiralOffset + Math.PI / 4; // Offset para Brazo de Orión

                x =
                    galacticCenter.x +
                    Math.cos(rotatedAngle) * armRadius +
                    (Math.random() - 0.5) * armWidth;
                y = (Math.random() - 0.5) * diskThickness;
                z =
                    galacticCenter.z +
                    Math.sin(rotatedAngle) * armRadius +
                    (Math.random() - 0.5) * armWidth;
                density = 2.0; // Medium-high density
            } else if (random < 0.85) {
                // Galactic disk (20% of stars)
                const diskAngle = Math.random() * Math.PI * 2;
                const diskRadius = 80000 + Math.random() * 250000;
                const diskHeight = (Math.random() - 0.5) * diskThickness;

                x = galacticCenter.x + Math.cos(diskAngle) * diskRadius;
                y = diskHeight;
                z = galacticCenter.z + Math.sin(diskAngle) * diskRadius;
                density = 1.5; // Medium density
            } else {
                // Galactic halo (15% of stars)
                const haloRadius = 150000 + Math.random() * 300000;
                const haloPhi = Math.random() * Math.PI * 2;
                const haloTheta = Math.acos(2 * Math.random() - 1);

                x = galacticCenter.x + haloRadius * Math.sin(haloTheta) * Math.cos(haloPhi);
                y = haloRadius * Math.cos(haloTheta);
                z = galacticCenter.z + haloRadius * Math.sin(haloTheta) * Math.sin(haloPhi);
                density = 0.5; // Low density
            }

            positions[i * 3] = x;
            positions[i * 3 + 1] = y;
            positions[i * 3 + 2] = z;

            // Generate star color based on galactic position and stellar evolution
            let starColor;
            if (this.config.colorVariation) {
                const colorIndex = this.getGalacticWeightedStarColorIndex(density);
                starColor = new THREE.Color(this.starColors[colorIndex]);
            } else {
                starColor = new THREE.Color(0xffffff);
            }

            // Apply brightness based on density and distance
            const distanceFromSun = Math.sqrt(
                (x - solarPosition.x) ** 2 + (y - solarPosition.y) ** 2 + (z - solarPosition.z) ** 2
            );

            // Brightness decreases with distance but increases with density
            const baseBrightness =
                this.config.minBrightness +
                Math.random() * (this.config.maxBrightness - this.config.minBrightness);
            const densityBoost = Math.min(density * 0.4, 0.6); // Increased density boost
            const distanceFactor = Math.max(0.2, 1.0 - distanceFromSun / 400000); // Adjusted for realistic distances

            const finalBrightness = Math.min(
                1.0,
                (baseBrightness * distanceFactor + densityBoost) * 1.0
            ); // Balanced brightness
            starColor.multiplyScalar(finalBrightness);

            colors[i * 3] = starColor.r;
            colors[i * 3 + 1] = starColor.g;
            colors[i * 3 + 2] = starColor.b;

            // Generate star size based on brightness and type
            const baseSize =
                this.config.minSize + Math.random() * (this.config.maxSize - this.config.minSize);
            const sizeMultiplier = 0.5 + finalBrightness * 1.0; // Smaller size multiplier
            sizes[i] = Math.min(3.0, baseSize * sizeMultiplier); // Reduced maximum size
        }

        // Set geometry attributes
        this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        this.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        this.geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

        // Create star material
        this.material = new THREE.PointsMaterial({
            size: 1.5, // Smaller base size for point-like appearance
            transparent: true,
            opacity: 0.9, // Slightly transparent for realistic look
            vertexColors: true,
            sizeAttenuation: false,
            blending: THREE.AdditiveBlending,
        });

        // Create the star field object
        this.starField = new THREE.Points(this.geometry, this.material);
        this.starField.name = 'starField';
        this.starField.visible = this.isVisible;

        // Add to scene
        this.scene.add(this.starField);

        // Apply realistic galactic orientation
        this.applyGalacticOrientation();

        console.log('Realistic galactic star distribution created with proper orientation');
    }

    /**
     * Get weighted star color index based on galactic position and stellar evolution
     * Different regions of the galaxy have different stellar populations
     * @param {number} density - Local stellar density (affects star formation)
     * @returns {number} Index into starColors array
     */
    getGalacticWeightedStarColorIndex(density) {
        const random = Math.random();

        // Adjust stellar populations based on galactic region
        if (density > 2.5) {
            // Galactic center - older, redder stars and some young blue giants
            if (random < 0.4) return 4; // M-type (red giants)
            if (random < 0.6) return 3; // K-type (orange giants)
            if (random < 0.75) return 0; // G-type
            if (random < 0.85) return 1; // F-type
            if (random < 0.95) return 5; // A-type
            return 6; // B-type (blue giants near galactic center)
        } else if (density > 1.5) {
            // Spiral arms - mix of young and old stars
            if (random < 0.5) return 4; // M-type
            if (random < 0.7) return 3; // K-type
            if (random < 0.85) return 0; // G-type
            if (random < 0.92) return 1; // F-type
            if (random < 0.97) return 5; // A-type
            if (random < 0.995) return 6; // B-type
            return 7; // O-type (very young, hot stars in spiral arms)
        } else if (density > 0.8) {
            // Galactic disk - similar to local neighborhood
            if (random < 0.76) return 4; // M-type
            if (random < 0.88) return 3; // K-type
            if (random < 0.96) return 0; // G-type
            if (random < 0.98) return 1; // F-type
            if (random < 0.994) return 5; // A-type
            if (random < 0.999) return 6; // B-type
            return 7; // O-type
        } else {
            // Galactic halo - very old, metal-poor stars
            if (random < 0.85) return 4; // M-type (old red dwarfs)
            if (random < 0.95) return 3; // K-type (old orange stars)
            return 0; // G-type (old yellow stars)
        }
    }

    /**
     * Dispose of star field resources
     */
    dispose() {
        if (this.starField && this.scene) {
            this.scene.remove(this.starField);
        }

        if (this.geometry) {
            this.geometry.dispose();
            this.geometry = null;
        }

        if (this.material) {
            this.material.dispose();
            this.material = null;
        }

        this.starField = null;
        this.scene = null;

        console.log('Star field disposed');
    }

    /**
     * Get current star field object
     * @returns {THREE.Points|null} Star field object
     */
    getStarField() {
        return this.starField;
    }

    /**
     * Check if star field is initialized
     * @returns {boolean} True if initialized
     */
    isInitialized() {
        return this.starField !== null;
    }
}
